import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import clsx from 'clsx';

import type { BoPostingStateDetail } from '@/types';

import {
  boPostingCopy,
  RECONCILABLE_STATES,
  RECORD_FROM_BO_COPY,
  SETTLE_MANUALLY_COPY,
} from '@/utils/trades/bo-posting-copy';

/**
 * LR-711: what BackOffice did with this trade's last posting, and what to do next.
 *
 * The trade page's answer to a class of stall that had no answer here at all. When
 * a posting to BackOffice fails or loses its reply, the trade stops moving — and
 * before this panel the only record of it lived in Django-admin, so the operator
 * looking at the trade saw a status that never changed and no reason for it.
 * TRD-111 sat that way for 25 days.
 *
 * Four states, and the panel refuses to blur them, because they differ in the one
 * fact that decides what a person may safely do:
 *
 *   rejected  — BackOffice judged the payload and refused it. Nothing was booked;
 *               the trade is wrong and posting it again unchanged fails the same
 *               way.
 *   failed    — BackOffice never answered or errored. Nothing was booked; a retry
 *               is what helps, and the crons do it on their own.
 *   timed_out — the outcome is unknown. BackOffice MAY hold the booking, so
 *               "just try again" can book the trade twice.
 *   confirmed_unrecorded — BackOffice DID book it and our own write was lost, so
 *               the two books disagree right now. Not a failed posting at all
 *               (LR-709 round 2), which is why it must not share a sentence with
 *               `timed_out`: there we say "may", here we know.
 *
 * The last two are the ones that offer Reconcile — a blind repost is unsafe for
 * both, and the re-POST's own answer (409, or 200) is what settles them.
 *
 * Read-only by default. `onReconcile` is offered only for the states that need a
 * decision; releasing the guard by hand stays an administrator's action and is not
 * surfaced here (LR-711 backend gates it on CAP_ADMIN).
 *
 * LR-715 adds the one exit BackOffice cannot open. When the re-POST is answered
 * *200* — BackOffice booking a request id it had already accepted, which it should
 * refuse forever — the row is deliberately left open, and nothing BackOffice says
 * later can close it. `onSettleManually` is that dead end's only door, offered on
 * `is_contradicted` alone and gated on CAP_ADMIN by the caller.
 *
 * LR-706 (g) adds the exit for the posting Reconcile can never settle: one sent
 * before `extraData.requestId` existed. BackOffice cannot recognise a repeat of it,
 * so it is never re-sent — instead BackOffice is READ and our side is written from
 * what it holds. `onRecordFromBackoffice` is that action, admin-gated by the caller
 * and offered where `is_legacy` says the posting has no other way out.
 *
 * On a legacy `confirmed_unrecorded` row those two doors stand side by side, and
 * they are not the same door twice: recording writes our book from what BackOffice
 * holds, closing by hand says the operator sorted it out in BackOffice itself and
 * there is nothing left to record. The copy of each says which is which.
 */
export interface BoPostingPanelProps {
  posting: BoPostingStateDetail;
  /**
   * Offered for `timed_out` and `confirmed_unrecorded` — the two states a re-POST
   * can settle. Omit to render the panel read-only.
   */
  onReconcile?: () => void;
  isReconciling?: boolean;
  /**
   * Offered only where BackOffice contradicted itself (`is_contradicted`) or no
   * proof can ever arrive, and only to an administrator. Omit for everyone else: the panel still says what
   * happened, because the fact is the operator's business even when the remedy is
   * not theirs.
   */
  onSettleManually?: () => void;
  isSettling?: boolean;
  /**
   * LR-706 (g): offered only on a LEGACY posting BackOffice already holds, and
   * only to an administrator. Omit for everyone else — the panel still says what
   * happened, because the fact is the operator's business even when the remedy is
   * not theirs.
   */
  onRecordFromBackoffice?: () => void;
}

export function BoPostingPanel({
  posting,
  onReconcile,
  isReconciling = false,
  onSettleManually,
  isSettling = false,
  onRecordFromBackoffice,
}: BoPostingPanelProps) {
  const [copied, setCopied] = useState(false);
  const { title, detail, tone, cleared } = boPostingCopy(posting);
  // `is_blocked === false` means a reconcile already happened and the re-post is
  // scheduled — offering the button again would 400 on the server ("nothing
  // blocked") and read to the operator as if the first click had failed.
  // `undefined` (an older payload) keeps the button, which is the safe side: the
  // action itself cannot double-book.
  // `confirmed_unrecorded` needs it as much as `timed_out` — more, in fact: there
  // the books are KNOWN to disagree, and the re-POST's 409 is what closes them.
  // Except once BackOffice has proved it will NOT refuse the repeat
  // (`is_contradicted`, LR-715): sending the same id a third time is how a second
  // duplicate is booked. `is_blocked === false` already hides the button on that
  // path today — the successful re-POST cleared the guard — but the gate must not
  // depend on that coincidence, because `is_blocked` is `undefined` on an older
  // payload and the whole point of this state is that BackOffice is not behaving
  // as its contract says.
  // An unstamped `confirmed_unrecorded` row is out (LR-715 round 2): BackOffice
  // already told us it holds the posting, and with no request id nothing can
  // re-ask it — reconciling returns the same answer and changes nothing.
  //
  // But ONLY that row (round 3). The first cut tested `has_request_id !== false`
  // without regard to state, which also hid the button on an unstamped
  // `timed_out` — where Reconcile does the most work of all: the endpoint routes
  // an unstamped block to `_classify_legacy_block`, which SEARCHES BackOffice
  // (`find_legacy_posting`) and records what it finds. That is LR-713's whole
  // feature, and the button is its on-demand door for an operator who is looking
  // at the trade now. Hiding it left that row with no action at all and copy
  // saying it is "blocked until that is settled".
  const proofImpossible =
    posting.state === 'confirmed_unrecorded' && posting.has_request_id === false;
  const canReconcile =
    RECONCILABLE_STATES.includes(posting.state) &&
    posting.is_blocked !== false &&
    !posting.is_contradicted &&
    !proofImpossible &&
    onReconcile != null;
  // LR-715. Not an alternative rendering of Reconcile but its opposite: reconciling
  // asks BackOffice again, and here BackOffice has already answered in a way no
  // further answer can settle. `is_contradicted` is the whole gate — the state is
  // implied by it (only a `confirmed_unrecorded` row can be contradicted), and
  // `undefined` on an older payload keeps the button away, which is the right side
  // for an irreversible admin action.
  //
  // LR-715 round 2 [Medium] #1: it is NOT the whole gate. The server deliberately
  // does not require proof of contradiction — because a legacy row can never
  // produce any, and requiring it would wall off the one population that has no
  // other way out. The button was gated on the proof anyway, so the door the
  // server left open had no handle on it. An unstamped row is admitted on the same
  // reasoning: `has_request_id === false` means no answer will ever settle it, and
  // a person is the only remaining way it closes. LR-713 made that population real.
  const canSettleManually =
    (posting.is_contradicted === true || proofImpossible) && onSettleManually != null;
  // LR-706 (g): the legacy posting's other exit, and the one that makes the books
  // agree rather than only closing the report about them. BackOffice is read, never
  // written to. Gated on the guard still holding, because that is what makes it a
  // live problem — a released guard means someone already decided this posting's
  // fate. It shares a row with `canSettleManually` on purpose: recording is for the
  // posting BackOffice still holds, closing by hand for the one the operator has
  // already sorted out there.
  //
  // Reconcile is not hidden here by `is_legacy` (LR-715 round 4 settled that): on an
  // unstamped `timed_out` row Reconcile is the on-demand read-back, and only the
  // `confirmed_unrecorded` case — `proofImpossible` above — has nothing left to ask.
  const canRecordFromBackoffice =
    posting.state === 'confirmed_unrecorded' &&
    posting.is_legacy === true &&
    posting.is_blocked !== false &&
    onRecordFromBackoffice != null;
  // Shown to everyone, handler or not: it is a fact about the trade, not an action.
  // Comes from `boPostingCopy` so it cannot disagree with the body text about whether
  // the guard is still on — see `Copy.cleared`.

  const handleCopy = () => {
    if (!posting.response_body) return;
    navigator.clipboard?.writeText(posting.response_body).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => {
        /* clipboard denied — the text is visible inline anyway */
      },
    );
  };

  return (
    <section
      role="alert"
      aria-labelledby="bo-posting-heading"
      data-testid="bo-posting-panel"
      className={clsx(
        'rounded-lg border px-4 py-3',
        tone === 'red' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50',
      )}
    >
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon
          className={clsx(
            'mt-0.5 h-5 w-5 flex-none',
            tone === 'red' ? 'text-red-500' : 'text-amber-500',
          )}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p
              id="bo-posting-heading"
              className={clsx(
                'text-sm font-medium',
                tone === 'red' ? 'text-red-800' : 'text-amber-800',
              )}
            >
              {title}
            </p>
            {posting.response_body && (
              <button
                type="button"
                onClick={handleCopy}
                className={clsx(
                  'flex-none rounded border bg-white px-2 py-0.5 text-[11px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                  tone === 'red'
                    ? 'border-red-200 text-red-700 hover:bg-red-100 focus-visible:ring-red-500'
                    : 'border-amber-200 text-amber-800 hover:bg-amber-100 focus-visible:ring-amber-500',
                )}
              >
                {copied ? 'Copied' : 'Copy response'}
              </button>
            )}
          </div>

          <p
            className={clsx(
              'mt-0.5 text-sm',
              tone === 'red' ? 'text-red-700' : 'text-amber-800',
            )}
          >
            {detail}
          </p>

          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-gray-600">
            {posting.attempted_at && (
              <div className="flex gap-1">
                <dt className="font-medium">Attempted</dt>
                <dd>{posting.attempted_at.slice(0, 16).replace('T', ' ')}</dd>
              </div>
            )}
            {posting.http_status != null && (
              <div className="flex gap-1">
                <dt className="font-medium">BackOffice status</dt>
                <dd>{posting.http_status}</dd>
              </div>
            )}
            {posting.value_date && (
              <div className="flex gap-1">
                <dt className="font-medium">Value date</dt>
                <dd>{posting.value_date}</dd>
              </div>
            )}
            {posting.external_id && (
              <div className="flex gap-1">
                <dt className="font-medium">BackOffice transaction</dt>
                <dd className="font-mono">{posting.external_id}</dd>
              </div>
            )}
          </dl>

          {posting.response_body && (
            // Focusable + labelled so a keyboard user can reach and scroll it —
            // the same treatment the LR-544 BO error banner gets.
            <pre
              tabIndex={0}
              role="region"
              aria-label="BackOffice response detail"
              className={clsx(
                'mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap break-all rounded border bg-white p-2 text-[11px] font-mono focus-visible:outline-none focus-visible:ring-2',
                tone === 'red'
                  ? 'border-red-200 text-red-700 focus-visible:ring-red-500'
                  : 'border-amber-200 text-amber-800 focus-visible:ring-amber-500',
              )}
            >
              {posting.response_body}
            </pre>
          )}

          {canReconcile && (
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={onReconcile}
                disabled={isReconciling}
                data-testid="bo-posting-reconcile"
                className="rounded border border-amber-300 bg-white px-2.5 py-1 text-[12px] font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1"
              >
                {isReconciling ? 'Reconciling…' : 'Reconcile with BackOffice'}
              </button>
              <p className="text-[11px] text-amber-800">
                Re-sends the posting under the same request id, so BackOffice books
                it only if it has not already.
              </p>
            </div>
          )}

          {canRecordFromBackoffice && (
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={onRecordFromBackoffice}
                data-testid="bo-posting-record-from-bo"
                className="rounded border border-red-300 bg-white px-2.5 py-1 text-[12px] font-medium text-red-800 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
              >
                {RECORD_FROM_BO_COPY.action}
              </button>
              <p className="text-[11px] text-red-800">{RECORD_FROM_BO_COPY.hint}</p>
            </div>
          )}

          {canSettleManually && (
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={onSettleManually}
                disabled={isSettling}
                data-testid="bo-posting-settle-manually"
                className="rounded border border-red-300 bg-white px-2.5 py-1 text-[12px] font-medium text-red-800 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
              >
                {isSettling ? 'Closing…' : SETTLE_MANUALLY_COPY.action}
              </button>
              <p className="text-[11px] text-red-800">{SETTLE_MANUALLY_COPY.hint}</p>
            </div>
          )}

          {cleared && (
            // Where the button was. The operator who just clicked Reconcile needs
            // to see that it worked — the attempt row itself will not say so until
            // BackOffice answers, and an unchanged panel reads as a failed click.
            <p
              data-testid="bo-posting-cleared"
              className={clsx(
                'mt-3 text-[11px] font-medium',
                tone === 'red' ? 'text-red-800' : 'text-amber-900',
              )}
            >
              {cleared}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
